import { lazy, Suspense } from "react";
import { createRootRoute, Outlet, redirect, useLocation } from "@tanstack/react-router";
import { fetchMe, isAuthenticated } from "@/lib/auth";
import { queryClient } from "@/lib/query-client";
import { AuthLoadingScreen } from "@/components/auth-loading-screen";

// AppLayout is only rendered for authenticated, non-public paths. Lazy-load
// it so the /login route doesn't pull in the sidebar, install-prompt, etc.
const AppLayout = lazy(() =>
  import("@/components/layout/app-layout").then((m) => ({ default: m.AppLayout }))
);

// Shared queryKey for the AdminAuth principal. The same key is used by
// `useAuth()` in components — routing `beforeLoad`s through
// `queryClient.ensureQueryData()` lets all consumers share a single network
// call instead of triple-fetching on first login.
const AUTH_ME_QUERY_KEY = ["auth", "me"] as const;
const AUTH_ME_STALE_MS = 5 * 60 * 1000;

const PUBLIC_PATHS = [
  "/login",
  "/login/verify",
  "/login/oauth-callback",
  "/signup",
];

function isPublicPath(pathname: string): boolean {
  return PUBLIC_PATHS.some(
    (p) => pathname === p || pathname.startsWith(p + "/")
  );
}

// Determine if the role is crossing into the wrong namespace, and if so return
// the correct redirect target. Returns null if no redirect is needed.
// agency_user is treated like client_user for routing purposes (both live
// in /app/*).
function getCrossRoleRedirect(
  pathname: string,
  kind: "staff" | "client_user" | "agency_user",
): string | null {
  if (kind === "staff" && pathname.startsWith("/app")) {
    const subPath = pathname.slice("/app".length) || "/";
    return `/admin${subPath}`;
  }
  if ((kind === "client_user" || kind === "agency_user") && pathname.startsWith("/admin")) {
    return "/app";
  }
  return null;
}

export const Route = createRootRoute({
  beforeLoad: async ({ location }) => {
    if (isPublicPath(location.pathname)) return;

    if (!isAuthenticated()) {
      throw redirect({ to: "/login" });
    }

    // Reuse the shared react-query cache so this beforeLoad, the route-level
    // beforeLoads, and the `useAuth()` hook all consume a single fetch
    // result. Before this, first-login could fire `fetchMe()` 3× back-to-back
    // (root → index → useAuth), which is the visible flicker users reported.
    const auth = await queryClient.ensureQueryData({
      queryKey: AUTH_ME_QUERY_KEY,
      queryFn: fetchMe,
      staleTime: AUTH_ME_STALE_MS,
    });
    if (!auth) {
      throw redirect({ to: "/login" });
    }

    const crossRoleTarget = getCrossRoleRedirect(location.pathname, auth.kind);
    if (crossRoleTarget) {
      throw redirect({ to: crossRoleTarget as never });
    }
  },
  component: RootComponent,
});

function RootComponent() {
  const location = useLocation();
  if (isPublicPath(location.pathname)) {
    return <Outlet />;
  }
  return (
    // Branded loading screen while the AppLayout chunk fetches. Was `null`,
    // which painted a blank white frame on first login and on hard refresh
    // — that's the "blank" / "stuck" symptom users reported. Keeping the
    // brand mark visible communicates "we're loading", not "we crashed".
    <Suspense fallback={<AuthLoadingScreen />}>
      <AppLayout>
        <Outlet />
      </AppLayout>
    </Suspense>
  );
}
